import { action, computed, observable, reaction } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { message } from 'antd';
import { flatten, isEqual, uniq } from 'lodash';
import { ProjectsRootVisualStore } from '../../common/stores';
import { ProjectApplicationDefinitionEditVisualStore } from '../../iota_applications/stores';
import { ConditionModel, InputGroupModel, BindingGroupModel } from '../models';
import { InputMeta } from '../../iota_applications/types';
import { ApplicationDefinitionConditional } from '../../iota_applications/types/ApplicationDefinition';
import {
    AdditionalClassificationDto as AdditionalClassificationDtoBase,
    AdditionalClassificationOptionDto,
    AdditionalClassificationRefreshPreviewDto,
    CreateEditConditionFormValues,
    CreateEditInputGroupFormValues,
    LayoutStructureDto,
    InputGroupDto,
    BindingGroupDto
} from '../types';
import ApplicationDefinitionConditionalService from '../services/ApplicationDefinitionConditionalService';
import { RcFile } from 'antd/lib/upload';
import { ImportInputsResponse } from '../../common/types/types';
import { endpoints } from '../../../config';
import { exportBindingGroups, exportConditions, getTabStructureType } from '../misc/IotaTabExporter';
import { IotaTabExport } from '../types/IotaTabExport';
import type { DraftSnapshot, ReadyTabImport } from '../types/TabImportDraft';
import { cloneJson } from '../types/TabImportDraft';
import TabImportStore from './TabImportStore';
import { findClassificationConflicts, formatClassificationConflicts } from '../misc/classificationImpact';
import { flattenClassifications } from '../misc/classifications';
import AdditionalClassificationVerificationStore from './AdditionalClassificationVerificationStore';
import { showImportWarnings } from '../../common/services/ImportWarnings';

interface ConditionFormValues extends CreateEditConditionFormValues {}

interface InputGroupFormValues extends CreateEditInputGroupFormValues {}

interface AdditionalClassification extends AdditionalClassificationDtoBase {}

export default class ApplicationDefinitionConditionalEditStore {
    readonly tabImport = new TabImportStore(this, this.service);
    readonly classificationVerification = new AdditionalClassificationVerificationStore(
        () =>
            this.project && this.applicationDefinition && this.canEditAdditionalClassifications
                ? {
                      projectId: this.project.id,
                      applicationId: this.applicationDefinition.id,
                      current: this.additionalClassifications
                  }
                : undefined,
        () => this.activeTab === 'additional-classifications',
        this.service
    );

    @computed get canMutateClassifications() {
        return this.canEditAdditionalClassifications && !this.tabImport.isCommitting;
    }
    @computed get canDeleteInputGroups() {
        return !this.tabImport.isCommitting;
    }

    @observable
    activeTab: string = 'conditions';

    @observable
    createEditConditionDialogVisible: boolean = false;

    @observable
    createEditInputGroupDialogVisible: boolean = false;

    @observable
    conditions: ConditionModel[] = [];

    @observable
    inputGroups: InputGroupModel[] = [];

    @observable
    isUpdating: boolean = false;

    @observable
    isLoading: boolean = false;

    @observable
    currentImportFile: RcFile | undefined = undefined;

    @observable
    isImportConfirmDialogVisible: boolean = false;

    @observable
    isImportingConditions: boolean = false;

    @observable
    isDataChanged: boolean = false;

    @observable
    addAdditionalClassificationModalVisible: boolean = false;

    @observable
    isLoadingAdditionalClassifications: boolean = false;

    @observable
    additionalClassificationOptions: AdditionalClassificationOptionDto[] = [];

    @observable
    additionalClassifications: AdditionalClassification[] = [];

    @observable
    selectedAdditionalClassification: AdditionalClassificationOptionDto | undefined = undefined;

    @observable
    previewAdditionalClassification: AdditionalClassification | undefined = undefined;

    @observable
    isLoadingAdditionalClassificationPreview: boolean = false;

    @observable
    additionalClassificationsError: string | undefined = undefined;

    @observable
    additionalClassificationsPreviewError: string | undefined = undefined;

    @observable
    additionalClassificationModalStep: 'select' | 'preview' = 'select';

    @observable
    canEditAdditionalClassifications: boolean = false;

    @observable
    isLoadingAdditionalClassificationRefresh: boolean = false;

    @observable
    refreshingAdditionalClassificationTypeCode: string | undefined = undefined;

    @observable
    additionalClassificationRefreshPreview: AdditionalClassificationRefreshPreviewDto | undefined = undefined;

    private classificationRefreshTarget: AdditionalClassification | undefined;

    private savedDraftSnapshot: DraftSnapshot | undefined;
    private classificationDraftsNeedReconciliation = false;

    @computed
    get additionalClassificationRefreshModalVisible(): boolean {
        return Boolean(this.additionalClassificationRefreshPreview);
    }

    @computed
    get project() {
        return this.projectsRootStore.currentProject;
    }

    @computed
    get applicationDefinition() {
        const applicationDefinition = this.applicationDefinitionEditVisualStore.currentAppDef;

        return applicationDefinition instanceof ApplicationDefinitionConditional ? applicationDefinition : null;
    }

    @computed
    get applicationDefinitionInputs(): InputMeta[] {
        if (!this.applicationDefinition) {
            return [];
        }

        if (this.applicationDefinitionEditVisualStore.inputs?.length) {
            return this.applicationDefinitionEditVisualStore.inputs;
        }

        return JSON.parse(this.applicationDefinition.meta).inputs;
    }

    @computed
    get applicationDefinitionLayoutStructure(): LayoutStructureDto | null {
        if (!this.applicationDefinition) {
            return null;
        }

        return JSON.parse(this.applicationDefinition.meta).layoutStructure;
    }

    @computed
    get applicationDefinitionSectionNames() {
        return uniq(this.applicationDefinitionInputs.filter(i => !!i.section).map(i => i.section!));
    }

    @computed
    get selectedCondition() {
        return this.conditions.find(c => c.selected);
    }

    @computed
    get editableCondition() {
        return this.conditions.find(c => c.editable);
    }

    @computed
    get editableInputGroup() {
        return this.inputGroups.find(g => g.editable);
    }

    @computed
    get importBindingsActionUrl() {
        if (!this.project || !this.applicationDefinition) {
            return '';
        }

        return (
            endpoints.manageUrl +
            `projects/${this.project.id}/iota/applications/${this.applicationDefinition.id}/import-conditions`
        );
    }

    @computed
    get fileImportActionHeaders() {
        return this.applicationDefinitionEditVisualStore.fileImportActionHeaders;
    }

    constructor(
        private readonly projectsRootStore: ProjectsRootVisualStore,
        readonly applicationDefinitionEditVisualStore: ProjectApplicationDefinitionEditVisualStore,
        private readonly service: ApplicationDefinitionConditionalService
    ) {
        reaction(
            () => this.applicationDefinition,
            applicationDefinition => {
                if (applicationDefinition) {
                    this.setActiveTab('conditions');
                    this.clearEditableData();
                    this.setEditableData(applicationDefinition);
                }
            }
        );
    }

    @action.bound
    setActiveTab(activeTab: string) {
        this.activeTab = activeTab;
    }

    @action.bound
    setCreateEditConditionDialogVisible(createEditConditionDialogVisible: boolean) {
        this.createEditConditionDialogVisible = createEditConditionDialogVisible;
    }

    @action.bound
    setCreateEditInputGroupDialogVisible(createEditInputGroupDialogVisible: boolean) {
        this.createEditInputGroupDialogVisible = createEditInputGroupDialogVisible;
    }

    @action.bound
    async openAddAdditionalClassificationModal() {
        if (!this.canEditAdditionalClassifications) {
            return;
        }

        this.addAdditionalClassificationModalVisible = true;
        this.selectedAdditionalClassification = undefined;
        await this.loadAdditionalClassificationOptions();
    }

    @action.bound
    closeAddAdditionalClassificationModal() {
        this.addAdditionalClassificationModalVisible = false;
        this.selectedAdditionalClassification = undefined;
        this.previewAdditionalClassification = undefined;
        this.additionalClassificationsError = undefined;
        this.additionalClassificationsPreviewError = undefined;
        this.additionalClassificationModalStep = 'select';
    }

    @action.bound
    setSelectedAdditionalClassification(option: AdditionalClassificationOptionDto | undefined) {
        this.selectedAdditionalClassification = option;
    }

    @action.bound
    async loadAdditionalClassificationOptions() {
        if (!this.project || !this.applicationDefinition) {
            return;
        }

        this.isLoadingAdditionalClassifications = true;
        this.additionalClassificationsError = undefined;

        try {
            const resp = await this.service.getAdditionalClassifications(
                this.project.id,
                this.applicationDefinition.id
            );

            if (resp.isOk()) {
                this.additionalClassificationOptions = this.filterConfiguredAdditionalClassificationOptions(resp.value);
            } else {
                this.additionalClassificationOptions = [];
                this.additionalClassificationsError = 'Failed to load additional classifications';
                message.error('Failed to load additional classifications');
            }
        } finally {
            this.isLoadingAdditionalClassifications = false;
        }
    }

    @action.bound
    async handleAdditionalClassificationNext() {
        if (
            !this.canEditAdditionalClassifications ||
            !this.project ||
            !this.applicationDefinition ||
            !this.selectedAdditionalClassification
        ) {
            return;
        }

        this.isLoadingAdditionalClassificationPreview = true;
        this.additionalClassificationsPreviewError = undefined;

        try {
            const resp = await this.service.getAdditionalClassificationPreview(
                this.project.id,
                this.applicationDefinition.id,
                this.selectedAdditionalClassification.typeCode
            );

            if (resp.isOk()) {
                this.previewAdditionalClassification = resp.value;
                this.additionalClassificationModalStep = 'preview';
            } else {
                this.additionalClassificationsPreviewError = 'Failed to load classification dependencies';
                message.error('Failed to load classification dependencies');
            }
        } finally {
            this.isLoadingAdditionalClassificationPreview = false;
        }
    }

    @action.bound
    handleAdditionalClassificationBack() {
        this.previewAdditionalClassification = undefined;
        this.additionalClassificationsPreviewError = undefined;
        this.additionalClassificationModalStep = 'select';
    }

    @action.bound
    confirmAdditionalClassification() {
        if (!this.canMutateClassifications || !this.previewAdditionalClassification) {
            return;
        }

        this.additionalClassifications = [
            ...this.additionalClassifications.filter(
                classification => classification.typeCode !== this.previewAdditionalClassification!.typeCode
            ),
            this.previewAdditionalClassification
        ];
        this.syncAdditionalClassificationInputs(this.retainedClassificationInputs);
        this.setIsDataChanged(true);
        this.closeAddAdditionalClassificationModal();
    }

    @action.bound
    removeAdditionalClassification(classification: AdditionalClassification) {
        if (!this.canMutateClassifications) {
            return;
        }

        this.additionalClassifications = this.additionalClassifications.filter(
            item => item.typeCode !== classification.typeCode
        );
        this.syncAdditionalClassificationInputs(this.retainedClassificationInputs);
        this.setIsDataChanged(true);
    }

    @action.bound
    async refreshAdditionalClassificationFromSics(classification: AdditionalClassification) {
        if (
            !this.canEditAdditionalClassifications ||
            !this.project ||
            !this.applicationDefinition ||
            this.isLoadingAdditionalClassificationRefresh ||
            !this.additionalClassifications.includes(classification)
        ) {
            return;
        }

        this.isLoadingAdditionalClassificationRefresh = true;
        this.refreshingAdditionalClassificationTypeCode = classification.typeCode;
        this.additionalClassificationRefreshPreview = undefined;
        this.classificationRefreshTarget = classification;
        const currentClassification = cloneJson(classification);

        try {
            const resp = await this.service.getAdditionalClassificationRefreshPreview(
                this.project.id,
                this.applicationDefinition.id,
                classification.typeCode,
                currentClassification
            );

            if (
                this.classificationRefreshTarget !== classification ||
                !this.additionalClassifications.includes(classification)
            ) {
                return;
            }
            if (resp.isOk()) {
                if (resp.value.isUpToDate) {
                    message.info('The classification is up to date.');
                    await this.classificationVerification.verify(true);
                    return;
                }

                this.additionalClassificationRefreshPreview = resp.value;
            } else {
                message.error('Failed to refresh classification from SICS');
            }
        } finally {
            this.isLoadingAdditionalClassificationRefresh = false;
            this.refreshingAdditionalClassificationTypeCode = undefined;
        }
    }

    @action.bound
    confirmAdditionalClassificationRefresh() {
        if (!this.canMutateClassifications || !this.additionalClassificationRefreshPreview) {
            return;
        }

        const target = this.classificationRefreshTarget;
        if (!target || !this.additionalClassifications.includes(target)) {
            this.closeAdditionalClassificationRefreshModal();
            return;
        }
        const { refreshed } = this.additionalClassificationRefreshPreview;
        this.additionalClassifications = this.additionalClassifications.map(classification =>
            classification === target ? refreshed : classification
        );
        this.syncAdditionalClassificationInputs(this.retainedClassificationInputs);
        this.setIsDataChanged(true);
        this.closeAdditionalClassificationRefreshModal();
    }

    @action.bound
    closeAdditionalClassificationRefreshModal() {
        this.classificationRefreshTarget = undefined;
        this.additionalClassificationRefreshPreview = undefined;
    }

    @action.bound
    moveAdditionalClassification(classification: AdditionalClassification, direction: -1 | 1) {
        if (!this.canEditAdditionalClassifications) {
            return;
        }

        const currentIndex = this.additionalClassifications.findIndex(
            item => item.typeCode === classification.typeCode
        );

        this.moveAdditionalClassificationToIndex(currentIndex, currentIndex + direction);
    }

    @action.bound
    moveAdditionalClassificationToIndex(currentIndex: number, nextIndex: number) {
        if (!this.canMutateClassifications) {
            return;
        }

        if (
            currentIndex === nextIndex ||
            currentIndex < 0 ||
            nextIndex < 0 ||
            currentIndex >= this.additionalClassifications.length ||
            nextIndex >= this.additionalClassifications.length
        ) {
            return;
        }

        const nextClassifications = [...this.additionalClassifications];
        const [classification] = nextClassifications.splice(currentIndex, 1);

        if (!classification) {
            return;
        }

        nextClassifications.splice(nextIndex, 0, classification);
        this.additionalClassifications = nextClassifications;
        this.syncAdditionalClassificationInputs(this.retainedClassificationInputs);
        this.setIsDataChanged(true);
    }

    @action.bound
    addNewCondition(formValues: ConditionFormValues) {
        const condition = new ConditionModel(this, {
            ...formValues,
            conditionId: uuidv4(),
            name: formValues.name.trim(),
            priority: this.conditions.length + 1
        });

        this.conditions.push(condition);
    }

    @action.bound
    addNewInputGroup(formValues: InputGroupFormValues) {
        const inputGroup = new InputGroupModel(this, {
            ...formValues,
            inputGroupId: uuidv4(),
            name: formValues.name.trim(),
            enabled: true,
            isDefault: false,
            bindingGroups: []
        });

        this.inputGroups.push(inputGroup);

        return inputGroup;
    }

    @action.bound
    removeCondition(condition: ConditionModel) {
        this.conditions.splice(this.conditions.indexOf(condition), 1);
    }

    @action.bound
    removeInputGroup(inputGroup: InputGroupModel) {
        if (!this.canDeleteInputGroups) {
            return;
        }
        this.inputGroups.splice(this.inputGroups.indexOf(inputGroup), 1);
    }

    @action.bound
    clearEditableData() {
        this.classificationVerification.reset();
        this.conditions = [];
        this.inputGroups = [];
        this.additionalClassifications = [];
        this.closeAdditionalClassificationRefreshModal();
        this.syncAdditionalClassificationInputs();
    }

    @action.bound
    setEditableData(applicationDefinition: ApplicationDefinitionConditional) {
        this.conditions = applicationDefinition.conditions.map(condition => new ConditionModel(this, condition));
        this.additionalClassifications = applicationDefinition.additionalClassifications ?? [];
        this.syncAdditionalClassificationInputs();

        this.tabImport.dispose();

        if (applicationDefinition.inputGroups.length) {
            this.inputGroups = applicationDefinition.inputGroups.map(group => new InputGroupModel(this, group));
        } else {
            this.createDefaultInputGroups();
        }
        this.savedDraftSnapshot = this.getDraftSnapshot();
    }

    @action.bound
    createDefaultInputGroups() {
        if (!this.applicationDefinitionLayoutStructure) {
            return;
        }

        this.inputGroups = this.applicationDefinitionLayoutStructure.defaultLayoutInputGroups.map(
            group =>
                new InputGroupModel(this, {
                    inputGroupId: uuidv4(),
                    name: group.name,
                    enabled: true,
                    isDefault: group.isDefault,
                    layoutSettingsId: group.layoutSettingsId,
                    bindingGroups: []
                })
        );
    }

    @action.bound
    async updateData() {
        try {
            if (!this.project || !this.applicationDefinition || this.tabImport.isBusy) {
                return;
            }

            const conflicts = this.reconcileClassificationDrafts();
            if (conflicts.length) {
                message.error(formatClassificationConflicts(conflicts, 'saving'));
                return;
            }
            this.isUpdating = true;

            const payload = {
                conditions: this.conditions.map(c => c.dto),
                inputGroups: this.inputGroups.map(i => i.dto),
                additionalClassifications: this.additionalClassifications,
                fieldSettings: this.applicationDefinitionEditVisualStore.getFieldSettingsPayload()
            };
            const resp = await this.service.updateData(this.project.id, this.applicationDefinition.id, payload);

            if (resp.isOk()) {
                const updatedApp = resp.value;
                this.applicationDefinition.tabImportRevision = updatedApp.tabImportRevision;
                this.applicationDefinition.conditions = cloneJson(payload.conditions);
                this.applicationDefinition.inputGroups = cloneJson(payload.inputGroups);
                this.applicationDefinition.additionalClassifications = cloneJson(payload.additionalClassifications);
                this.applicationDefinition.inputFieldSettings = cloneJson(payload.fieldSettings);
                this.savedDraftSnapshot = this.getDraftSnapshot();
                this.applicationDefinition.setLastUpdatedInfo(updatedApp.lastUpdatedBy, updatedApp.lastUpdatedTime);
                this.setIsDataChanged(false);
                message.success('Application has been successfully updated');
            } else {
                message.error('Failed to update application');
            }
        } finally {
            this.isUpdating = false;
        }
    }

    @action.bound
    exportTab(tabKey: string = this.activeTab) {
        const application = this.applicationDefinition;
        if (!application || !this.project) {
            return;
        }

        try {
            const header = {
                formatVersion: 1 as const,
                applicationId: application.applicationId,
                workflowId: application.workflowId,
                extension: application.extension
            };
            const tags = this.applicationDefinitionEditVisualStore.ruleTags;
            let file: IotaTabExport;
            let tabName: string;
            if (tabKey === 'conditions') {
                tabName = 'Conditions';
                file = {
                    ...header,
                    configurationType: 'conditions',
                    ...exportConditions(this.conditions, this.inputGroups, tags)
                };
            } else if (tabKey === 'additional-classifications') {
                tabName = 'Additional Classifications';
                file = {
                    ...header,
                    configurationType: 'additional-classifications',
                    content: this.additionalClassifications
                };
            } else {
                const group = this.inputGroups.find(item => item.tabKey === tabKey);
                if (!group) {
                    throw new Error('Cannot export tab: the selected input group is unavailable.');
                }
                const defaults = group.defaultBindingGroups;
                tabName = group.name;
                file = {
                    ...header,
                    configurationType: 'input-group',
                    structureType: getTabStructureType(group),
                    content: exportBindingGroups(
                        group.bindingGroups,
                        defaults,
                        tags,
                        this.applicationDefinitionEditVisualStore.environmentVariables ?? []
                    )
                };
            }
            this.service.exportTab(file, `${application.name}_${tabName}`);
        } catch (error) {
            message.error(error instanceof Error ? error.message : 'Tab export failed. Please try again.');
        }
    }

    @action.bound
    exportAppBindings() {
        if (!this.applicationDefinition || !this.project) {
            console.error('No current app def or project');
            return;
        }

        this.service.exportAppConditions(this.applicationDefinition.id, this.project.id);
    }

    @action.bound
    getUserName(userId: string) {
        return this.applicationDefinitionEditVisualStore.getUserNameById(userId);
    }

    @action.bound
    setHeaders() {
        return this.applicationDefinitionEditVisualStore.setHeaders();
    }

    @action.bound
    setCurrentImportFile(file: RcFile | undefined) {
        this.currentImportFile = file;
    }

    @action.bound
    handleImportResponse(response: ImportInputsResponse) {
        if (response.importedSuccessfully) {
            showImportWarnings(response);
            const appDefId = this.applicationDefinition!.id;
            this.applicationDefinitionEditVisualStore.setCurrentAppDef(undefined);

            // Needed for correct UI update
            setTimeout(() => {
                message.success('You have successfully updated the application Inputs bindings.');
                this.setCurrentImportFile(undefined);
                this.applicationDefinitionEditVisualStore.loadApplication(appDefId);
            }, 0);
            return;
        }

        if (!response.canProceed) {
            message.error(response.responseMessage);
            return;
        }

        if (response.canProceed) {
            this.setIsImportConfirmDialogVisible(true);
        }
    }

    @action.bound
    setIsImportingConditions(isImporting: boolean) {
        this.isImportingConditions = isImporting;
    }

    @action.bound
    setIsImportConfirmDialogVisible(visible: boolean) {
        this.isImportConfirmDialogVisible = visible;
    }

    @action.bound
    handleImportDialogCancel() {
        this.setIsImportConfirmDialogVisible(false);
        this.setCurrentImportFile(undefined);
    }

    @action.bound
    async handleImportDialogConfirm() {
        if (!this.applicationDefinition || !this.project || !this.currentImportFile) {
            return;
        }

        try {
            this.setIsImportingConditions(true);
            const resp = await this.service.commitAppBindings(
                this.applicationDefinition!.id,
                this.project.id,
                this.currentImportFile!
            );

            if (resp.isOk()) {
                showImportWarnings(resp.value);
                const appDefId = this.applicationDefinition!.id;
                this.applicationDefinitionEditVisualStore.setCurrentAppDef(undefined);

                // Needed for correct UI update
                setTimeout(() => {
                    this.applicationDefinitionEditVisualStore.loadApplication(appDefId);
                    this.handleImportDialogCancel();
                    message.success('You have successfully updated the application Inputs bindings.');
                }, 0);
            } else {
                message.error(resp.error.data);
                console.error(resp.error.data);
            }
        } catch (err) {
            message.error(err);
            console.error(err);
        } finally {
            this.setIsImportingConditions(false);
        }
    }

    @action.bound
    setIsDataChanged(isChanged: boolean) {
        this.isDataChanged = isChanged;
    }

    @action.bound
    setCanEditAdditionalClassifications(canEditAdditionalClassifications: boolean) {
        this.canEditAdditionalClassifications = canEditAdditionalClassifications;
    }

    navigateToList() {
        if (this.project) {
            this.projectsRootStore.navigateToApplicationDefinitionsPage(this.project);
        }
    }

    getGroupDraftSnapshot(group: InputGroupModel): InputGroupDto {
        return cloneJson(this.serializeGroupDraft(group));
    }

    getConditionsSnapshot() {
        return cloneJson(this.conditions.map(condition => condition.dto));
    }

    getClassificationsSnapshot() {
        return cloneJson(this.additionalClassifications);
    }

    getDraftSnapshot(): DraftSnapshot {
        return cloneJson({
            conditions: this.conditions.map(condition => condition.dto),
            inputGroups: this.inputGroups.map(group => this.serializeGroupDraft(group)),
            additionalClassifications: this.additionalClassifications,
            fieldSettings: this.applicationDefinitionEditVisualStore.getFieldSettingsPayload()
        });
    }

    getClassificationConflicts(classifications: AdditionalClassificationDtoBase[]) {
        const visual = this.applicationDefinitionEditVisualStore;
        return findClassificationConflicts(
            this.additionalClassifications,
            classifications,
            this.applicationDefinitionInputs,
            visual.previewAdditionalClassificationInputs(classifications),
            this.inputGroups.map(group => this.serializeGroupDraft(group)),
            visual.getFieldSettingsPayload()
        );
    }

    @action.bound
    applyCommittedTab(imported: ReadyTabImport) {
        switch (imported.kind) {
            case 'additional-classifications': {
                const retain = this.getClassificationConflicts(imported.content).length > 0;
                const previous = this.currentClassificationInputs;
                const drafts = this.inputGroups.map(group => this.getGroupDraftSnapshot(group));
                if (isEqual(this.getClassificationsSnapshot(), imported.targetSnapshot)) {
                    this.additionalClassifications = cloneJson(imported.content);
                }
                this.syncAdditionalClassificationInputs(retain ? previous : this.retainedClassificationInputs, drafts);
                break;
            }
            case 'conditions':
                if (isEqual(this.getConditionsSnapshot(), imported.targetSnapshot)) {
                    this.conditions = imported.content.map(condition => new ConditionModel(this, condition));
                }
                break;
            case 'input-group': {
                const target = this.inputGroups.find(group => group.inputGroupId === imported.targetId);
                if (
                    target &&
                    isEqual(this.getGroupDraftSnapshot(target).bindingGroups, imported.targetSnapshot.bindingGroups)
                ) {
                    target.setBindingGroups(
                        new InputGroupModel(this, {
                            ...target.dto,
                            bindingGroups: imported.content.bindingGroups
                        }).bindingGroups
                    );
                }
            }
            // no default
        }
        this.applyImportedBaseline(imported);
        this.refreshDirtyState();
    }

    @action.bound
    refreshDirtyState() {
        this.isDataChanged = !this.savedDraftSnapshot || !isEqual(this.getDraftSnapshot(), this.savedDraftSnapshot);
    }

    private serializeGroupDraft(group: InputGroupModel): InputGroupDto {
        const rows = (groups: BindingGroupModel[]): BindingGroupDto[] =>
            groups.map(row => ({
                bindingGroupId: row.bindingGroupId,
                name: row.name,
                enabled: row.enabled,
                bindings: flatten(row.inputs.map(input => input.bindings)),
                childBindingGroups: rows(row.childBindingGroups)
            }));
        return {
            inputGroupId: group.inputGroupId,
            name: group.name,
            enabled: group.enabled,
            isDefault: group.isDefault,
            layoutSettingsId: group.layoutSettingsId,
            bindingGroups: rows(group.bindingGroups)
        };
    }

    @action.bound
    private applyImportedBaseline(imported: ReadyTabImport) {
        if (!this.savedDraftSnapshot) {
            return;
        }
        switch (imported.kind) {
            case 'conditions':
                this.savedDraftSnapshot.conditions = cloneJson(imported.content);
                break;
            case 'additional-classifications':
                this.savedDraftSnapshot.additionalClassifications = cloneJson(imported.content);
                this.savedDraftSnapshot.inputGroups = this.savedDraftSnapshot.inputGroups.map(group =>
                    this.getGroupDraftSnapshot(new InputGroupModel(this, group))
                );
                break;
            case 'input-group': {
                const group = this.getGroupDraftSnapshot(new InputGroupModel(this, imported.content));
                const index = this.savedDraftSnapshot.inputGroups.findIndex(g => g.inputGroupId === group.inputGroupId);
                if (index < 0) {
                    this.savedDraftSnapshot.inputGroups.push(group);
                } else {
                    this.savedDraftSnapshot.inputGroups[index] = group;
                }
            }
            // no default
        }
    }

    @action.bound
    private reconcileClassificationDrafts() {
        if (!this.classificationDraftsNeedReconciliation) {
            return [];
        }
        const conflicts = this.getClassificationConflicts(this.additionalClassifications);
        if (conflicts.length) {
            return conflicts;
        }
        const drafts = this.inputGroups.map(group => this.getGroupDraftSnapshot(group));
        this.syncAdditionalClassificationInputs(undefined, drafts);
        return [];
    }

    private get currentClassificationInputs() {
        return {
            inputs: this.applicationDefinitionInputs,
            settings: this.applicationDefinitionEditVisualStore.fieldSettings
        };
    }

    private get retainedClassificationInputs() {
        return this.classificationDraftsNeedReconciliation ? this.currentClassificationInputs : undefined;
    }

    @action.bound
    private syncAdditionalClassificationInputs(
        retain?: { inputs: InputMeta[]; settings: ProjectApplicationDefinitionEditVisualStore['fieldSettings'] },
        drafts?: InputGroupDto[]
    ) {
        this.applicationDefinitionEditVisualStore.syncAdditionalClassificationInputs(
            this.additionalClassifications,
            retain
        );
        this.classificationDraftsNeedReconciliation = Boolean(retain);
        this.rebuildInputGroups(drafts);
    }

    @action.bound
    private rebuildInputGroups(drafts = this.inputGroups.map(group => this.getGroupDraftSnapshot(group))) {
        this.inputGroups = drafts.map(group => new InputGroupModel(this, group));
    }

    private filterConfiguredAdditionalClassificationOptions(
        options: AdditionalClassificationOptionDto[]
    ): AdditionalClassificationOptionDto[] {
        const configuredClassifications = flattenClassifications(this.additionalClassifications);
        const configuredTypeCodes = new Set(configuredClassifications.map(classification => classification.typeCode));
        const configuredSlots = new Set(
            configuredClassifications.map(classification => classification.classificationSlot)
        );

        return options.filter(
            option => !configuredTypeCodes.has(option.typeCode) && !configuredSlots.has(option.classificationSlot)
        );
    }
}
